import { JSONSchema9 } from "@src/lib/types/jsonSchema";
import { WalterApi } from "@src/lib/walter/api";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import debug from "@src/lib/debug";
import { getCurrentWorkspace } from "@src/lib/walter/authContext";
import { defaultStorage } from "@src/lib/settings/defaultStorage";
import { WalterUser, WalterWorkspace } from "@src/lib/auth/OAuthService";

export class TemplateObjectsService {
  private static instance: TemplateObjectsService;
  private schema: JSONSchema9 | null = null;
  private loading: Promise<JSONSchema9 | null> | null = null;

  private constructor() {}

  static getInstance(): TemplateObjectsService {
    if (!TemplateObjectsService.instance) {
      TemplateObjectsService.instance = new TemplateObjectsService();
    }
    return TemplateObjectsService.instance;
  }

  /**
   * Get the template objects schema, loading it if necessary.
   * This method ensures the schema is loaded during app initialization.
   */
  async getSchema(currentUser?: WalterUser, currentWorkspace?: WalterWorkspace): Promise<JSONSchema9 | null> {
    if (this.schema) {
      return this.schema;
    }

    if (this.loading) {
      return this.loading;
    }

    const user = currentUser ?? defaultStorage.get<WalterUser>("currentUser");

    if (!user) {
      debug.warn("TemplateObjectsService: No current user found");
      return null;
    }

    const workspace =
      currentWorkspace ??
      getCurrentWorkspace(user, () => {
        debug.warn("TemplateObjectsService: No workspace available");
        return null;
      });

    if (!workspace) {
      debug.warn("TemplateObjectsService: No workspace found");
      return null;
    }

    this.loading = this.loadSchema(workspace);

    try {
      this.schema = await this.loading;
      return this.schema;
    } finally {
      this.loading = null;
    }
  }

  /**
   * Get the schema synchronously. Should only be called after initialization. Safe to call before authentication.
   */
  getSchemaSync(): JSONSchema9 | null {
    return this.schema;
  }

  /**
   * Check if the schema has been loaded
   */
  isLoaded(): boolean {
    return this.schema !== null;
  }

  /**
   * Force refresh the schema from the API
   */
  async refresh(): Promise<JSONSchema9 | null> {
    this.schema = null;
    return this.getSchema();
  }

  /**
   * Clear the cached schema (useful for testing or workspace changes)
   */
  clearCache(): void {
    this.schema = null;
    this.loading = null;
  }

  private async loadSchema(currentWorkspace: WalterWorkspace): Promise<JSONSchema9 | null> {
    debug.log(() => "TemplateObjectsService: Loading schema from API");

    try {
      const { response } = WalterApi.getTemplateObjects(currentWorkspace.id);
      const schema = await response;

      if (!schema) {
        debug.warn("TemplateObjectsService: API returned null");
        return null;
      }

      debug.log(() => "TemplateObjectsService: Successfully loaded schema from API");
      return schema;
    } catch (error) {
      captureErrorInfo(error, {
        extra: {
          context: "TemplateObjectsService.loadSchema",
        },
        level: "warning",
      });

      debug.warn("TemplateObjectsService: Failed to load schema from API, using fallback", error);
      return null;
    }
  }
}

export const templateObjectsService = TemplateObjectsService.getInstance();
